/**
 * ItemsSection — Mitbringsel-Liste, gemeinsam fuer alle Rollen.
 *
 * Admin: Drag&Drop, Add-Form, voller ⋯-Menu.
 * User:  Claim/Release-Buttons, eigener Kommentar bei isMine.
 * Guest: nur Lesen.
 */

import { useMemo, useState, type FormEvent } from "react";
import { Plus } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Button } from "@/components/Button";
import { Input, Textarea } from "@/components/Input";
import { EventItemCard } from "./EventItemCard";
import type { GuestItem, Item, Participant } from "@/lib/api";
import styles from "./ItemsSection.module.css";

type AdminProps = {
  viewerRole: "admin";
  items: Item[];
  participants: Participant[];
  onAdd: (payload: { name: string; comment?: string; forAllUsers?: boolean }) => void;
  onEdit: (id: number, payload: { name?: string; comment?: string | null; forAllUsers?: boolean }) => void;
  onAssign: (id: number, participantId: number | null, forAll: boolean) => void;
  onDuplicate: (id: number) => void;
  onDelete: (id: number) => void;
  onReorder: (updates: Array<{ id: number; position: number }>) => void;
  isAddPending?: boolean;
};

type UserProps = {
  viewerRole: "user";
  items: Item[];
  participants: Participant[];
  selfId: number;
  selfName: string;
  disabled?: boolean;
  onClaim: (id: number) => void;
  onRelease: (id: number) => void;
  onSetUserComment: (id: number, comment: string | null) => void;
  onAddItem?: (payload: { name: string; comment?: string }) => void;
  canAddItem?: boolean;
  isAddPending?: boolean;
};

type GuestProps = {
  viewerRole: "guest";
  items: GuestItem[];
  participants: Participant[];
};

export type ItemsSectionProps = AdminProps | UserProps | GuestProps;

function sortByPosition<T extends { position: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.position - b.position);
}

export function ItemsSection(props: ItemsSectionProps) {
  const { t } = useTranslation();

  return (
    <section className={styles.section}>
      <div className={styles.head}>
        <h2 className={styles.heading}>{t("event.itemsHeading")}</h2>
      </div>
      {props.items.length === 0 ? (
        <div className={styles.empty}>{t("admin.noItemsYet")}</div>
      ) : (
        <ItemsList {...props} />
      )}
      {props.viewerRole === "admin" && <AddItemForm onAdd={props.onAdd} isPending={props.isAddPending} />}
      {props.viewerRole === "user" && props.canAddItem && props.onAddItem && (
        <UserAddItemForm onAdd={props.onAddItem} isPending={props.isAddPending} />
      )}
    </section>
  );
}

function UserAddItemForm({
  onAdd,
  isPending,
}: {
  onAdd: (payload: { name: string; comment?: string }) => void;
  isPending?: boolean;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim()) return;
    onAdd({ name: name.trim(), comment: comment.trim() || undefined });
    setName("");
    setComment("");
  };

  return (
    <form className={styles.addForm} onSubmit={submit}>
      <div className={styles.addFields}>
        <Input
          label={t("user.addItemName")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
        />
        <Textarea
          label={t("user.addItemComment")}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={2}
          maxLength={300}
        />
      </div>
      <Button type="submit" size="compact" disabled={!name.trim() || isPending}>
        <Plus size={16} weight="regular" />
        {t("user.addItem")}
      </Button>
    </form>
  );
}

function ItemsList(props: ItemsSectionProps) {
  if (props.viewerRole === "admin") {
    return <AdminList {...props} />;
  }
  if (props.viewerRole === "user") {
    return (
      <div className={styles.list}>
        {sortByPosition(props.items).map((item) => (
          <EventItemCard
            key={item.id}
            viewerRole="user"
            item={item}
            participants={props.participants}
            selfId={props.selfId}
            selfName={props.selfName}
            disabled={props.disabled}
            onClaim={props.onClaim}
            onRelease={props.onRelease}
            onSetUserComment={props.onSetUserComment}
          />
        ))}
      </div>
    );
  }
  return (
    <div className={styles.list}>
      {sortByPosition(props.items).map((item) => (
        <EventItemCard
          key={item.id}
          viewerRole="guest"
          item={item}
          participants={props.participants}
        />
      ))}
    </div>
  );
}

function AdminList({
  items,
  participants,
  onEdit,
  onAssign,
  onDuplicate,
  onDelete,
  onReorder,
}: AdminProps) {
  const sortedItems = useMemo(() => sortByPosition(items), [items]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = sortedItems.findIndex((it) => it.id === active.id);
    const newIdx = sortedItems.findIndex((it) => it.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(sortedItems, oldIdx, newIdx);
    const positions = sortedItems.map((it) => it.position).sort((a, b) => a - b);
    const updates = reordered
      .map((it, i) => ({ id: it.id, position: positions[i] }))
      .filter((u, i) => u.position !== reordered[i].position);
    if (updates.length > 0) onReorder(updates);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={sortedItems.map((it) => it.id)} strategy={verticalListSortingStrategy}>
        <div className={styles.list}>
          {sortedItems.map((item) => (
            <EventItemCard
              key={item.id}
              viewerRole="admin"
              item={item}
              participants={participants}
              onEdit={onEdit}
              onAssign={onAssign}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function AddItemForm({
  onAdd,
  isPending,
}: {
  onAdd: AdminProps["onAdd"];
  isPending?: boolean;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [forAll, setForAll] = useState(false);

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim()) return;
    onAdd({ name: name.trim(), comment: comment.trim() || undefined, forAllUsers: forAll });
    setName("");
    setComment("");
    setForAll(false);
  };

  return (
    <form className={styles.addForm} onSubmit={submit}>
      <div className={styles.addFields}>
        <Input
          label={t("admin.itemName")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
        />
        <Textarea
          label={t("admin.itemComment")}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={2}
          maxLength={300}
        />
        <label className={styles.checkbox}>
          <input
            type="checkbox"
            checked={forAll}
            onChange={(e) => setForAll(e.target.checked)}
          />
          {t("admin.itemForAllUsers")}
        </label>
      </div>
      <Button type="submit" size="compact" disabled={!name.trim() || isPending}>
        <Plus size={16} weight="regular" />
        {t("admin.addItem")}
      </Button>
    </form>
  );
}
